import { buildSparseTree } from 'modules/agreement/discountTree';
import {
  contexts,
  type ClientAgreementDiscountRule,
  type ClientDiscountContext,
  type ClientDiscountPair,
  type ClientDiscountTreeNode,
  type ClientDiscountRootNode,
} from './types';
import type { ClientAgreementVendorTerms } from '../vendorTerms/types';
import { clientDiscountOwner } from './owner';
import { contextFields } from './form';
import { clientTargetKey, clientTargetPathFromProduct } from './clientTarget';

export const pathFromProduct = clientTargetPathFromProduct;
export const endpointKey = clientTargetKey;

/** Return the selected context pair without allowing values from another context to leak in. */
export function contextPair(rule: ClientAgreementDiscountRule, context: ClientDiscountContext): ClientDiscountPair {
  return {
    client: rule[contextFields[context].client],
    provider: rule[contextFields[context].provider],
  };
}

/**
 * Build only the authored tree. Discovery paths are deliberately not accepted here:
 * a client rule remains visible even when its saved target is absent from discovery.
 */
export function buildTree(rules: readonly ClientAgreementDiscountRule[]): ClientDiscountTreeNode[] {
  return buildSparseTree(rules);
}

/** Keep persisted roots, including empty roots and saved targets needing repair. */
export function buildRootTree(roots: readonly ClientAgreementVendorTerms[]): ClientDiscountRootNode[] {
  return roots.map((root) => {
    const owner = clientDiscountOwner(root.owner);
    const rootId = `${owner.kind}-${owner.id}`;
    const authored = buildTree(root.discount_rates);
    const matching = authored.find((node) => node.id === rootId);
    const attach = (node: ClientDiscountTreeNode, isRoot = false): ClientDiscountRootNode => ({
      ...node,
      id: `section-${root.id}:${node.id}`,
      root,
      owner,
      isRoot,
      children: node.children.map((child) => attach(child)),
    });
    return attach(
      {
        id: rootId,
        kind: owner.kind,
        label: owner.name,
        rules: matching?.rules ?? [],
        children: [...(matching?.children ?? []), ...authored.filter((node) => node !== matching)],
      },
      true,
    );
  });
}

/** The contexts used by the transport, in display/resolution order. */
export { contexts };
